/* ==========================================================================
   KUDU OUTDOOR LIVING — BEHAVIOUR
   No libraries. Every component below must still work with this file absent:
   the nav is real links, the FAQ is real <details>, the drawer target is a
   real anchor, and the reveal state only engages once html.k-js is set.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.add('k-js');

  var mqDesktop = window.matchMedia('(min-width: 1080px)');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------- scroll container
     THE HOST-THEME TRAP. Horizon does this at >=990px:

        html:has(.page-wrapper), html:has(.page-wrapper) body {
          height: 100dvh; overflow: hidden;
        }
        .page-wrapper { height: 100dvh; overflow-y: auto; }

     So above 990px the WINDOW DOES NOT SCROLL — .page-wrapper is the scroll
     container. A listener bound only to `window` never fires on desktop, which
     left the header permanently transparent while content slid underneath it.

     Below 990px Horizon leaves those rules off and the window scrolls normally.
     The breakpoint is the host theme's, not ours, so we detect the container at
     runtime rather than hard-coding 990.                                     */
  var pageWrapper = document.querySelector('.page-wrapper');

  function scrollerEl() {
    if (!pageWrapper) return null;
    var oy = getComputedStyle(pageWrapper).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1) {
      return pageWrapper;
    }
    return null;
  }

  function scrollPos() {
    var el = scrollerEl();
    if (el) return el.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  /* Bind to BOTH so the correct one always fires, whichever is live at the
     current viewport, and across resizes that cross the host breakpoint. */
  function onScroll(fn) {
    window.addEventListener('scroll', fn, { passive: true });
    if (pageWrapper) pageWrapper.addEventListener('scroll', fn, { passive: true });
  }

  /* ---------------------------------------------------------------- header */
  (function header() {
    var el = document.querySelector('[data-kudu-header]');
    if (!el) return;
    var ticking = false;

    function apply() {
      el.setAttribute('data-scrolled', scrollPos() > 24 ? 'true' : 'false');
      ticking = false;
    }

    /* Publish the measured header height so scroll-padding-top can offset
       anchor destinations by the real value rather than a guess. Measured
       after fonts settle, because the wordmark's metrics affect the height.
       The homepage overlay ALSO consumes this: kudu-ground.css pulls the hero
       up by exactly --k-header-h so the hero starts at the top of the page. */
    function measureHeader() {
      var h = el.offsetHeight;
      if (h > 0) document.documentElement.style.setProperty('--k-header-h', h + 'px');
    }

    apply();
    measureHeader();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { measureHeader(); apply(); });
    }
    window.addEventListener('resize', measureHeader, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(measureHeader).observe(el);

    onScroll(function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    });
  })();

  /* ------------------------------------------------- desktop mega disclosure
     THE CLICK BUG, as shipped: mouseenter opened the panel, then the click
     handler toggled it — so a real mouse click on a CLOSED menu ran
     mouseenter (open) then click (toggle -> close) and the menu appeared to
     do nothing. Pointer intent and click intent were fighting over one flag.

     Now there are two states. `open` is a transient hover state; `latched` is
     a deliberate click/keyboard state that hover cannot revoke. Clicking a
     hover-opened menu latches it rather than closing it; clicking a latched
     menu closes it. Leaving with the pointer only closes an unlatched menu,
     and only after a short grace period, so the 10px CSS bridge plus this
     delay make the trip from button to panel survivable.

     Enter and Space need no special handling: a real <button> fires `click`
     for both, and swallowing the keydown would suppress that click.        */
  (function mega() {
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-kudu-navitem]'));
    if (!items.length) return;

    var HOVER_GRACE = 220;
    var registry = [];

    function closeAll(except) {
      registry.forEach(function (r) {
        if (r.item === except) return;
        r.set(false);
      });
    }

    items.forEach(function (item) {
      var toggle = item.querySelector('[data-kudu-navtoggle]');
      var panel = item.querySelector('[data-kudu-navpanel]');
      if (!toggle || !panel) return;

      var latched = false;
      var timer = null;

      function links() {
        return Array.prototype.filter.call(
          panel.querySelectorAll('a[href]'),
          function (a) { return a.offsetParent !== null || item.getAttribute('data-open') === 'true'; }
        );
      }

      function set(state, latch) {
        if (timer) { clearTimeout(timer); timer = null; }
        item.setAttribute('data-open', state ? 'true' : 'false');
        toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
        latched = state ? !!latch || latched : false;
        if (state) { closeAll(item); place(); }
      }

      /* Keep the panel inside the viewport. A wide panel anchored to a nav
         item that sits left of centre can render with a NEGATIVE left edge —
         links present, focusable, and physically off the screen. Overflowing
         to the left produces no scrollbar, so nothing flags it. Measure the
         box (a visibility:hidden element still has one) and write the
         correction to --mega-dx, which the CSS transform already carries, so
         the open/close animation is untouched. */
      function place() {
        /* Do NOT reset --mega-dx and then measure. A custom property that
           feeds a transitioning `transform` is resolved on the compositor, so
           the rect read straight after the reset still carries the OLD shift —
           the panel measures as already-inside the viewport and the correction
           is thrown away. (Symptom: the first placement was right and every
           one after it silently reverted to 0.) Subtract the current shift
           arithmetically instead; that needs no flush and cannot go stale. */
        var cur = parseFloat(panel.style.getPropertyValue('--mega-dx')) || 0;
        var pad = 16;
        var vw = document.documentElement.clientWidth;
        var r = panel.getBoundingClientRect();
        var left = r.left - cur;
        var right = r.right - cur;
        var dx = 0;
        if (left < pad) dx = pad - left;
        else if (right > vw - pad) dx = Math.min(0, (vw - pad) - right);
        panel.style.setProperty('--mega-dx', Math.round(dx) + 'px');
      }

      var reg = { item: item, set: function (s) { set(s); }, isLatched: function () { return latched; } };
      registry.push(reg);

      set(false);
      place();
      window.addEventListener('resize', place, { passive: true });

      /* Click / Enter / Space. */
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var isOpen = item.getAttribute('data-open') === 'true';
        if (isOpen && latched) {
          set(false);
        } else {
          set(true, true);
        }
      });

      /* Pointer. Hover opens without latching, so it still closes on the way
         out — but a click while hovering promotes it to latched. */
      item.addEventListener('mouseenter', function () {
        if (!mqDesktop.matches) return;
        if (timer) { clearTimeout(timer); timer = null; }
        set(true);
      });
      item.addEventListener('mouseleave', function () {
        if (!mqDesktop.matches) return;
        if (latched) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { set(false); }, HOVER_GRACE);
      });

      /* Keyboard. ArrowDown from the button enters the panel; Escape closes
         and returns focus to the button that opened it. */
      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'Down') {
          e.preventDefault();
          set(true, true);
          var l = links();
          if (l.length) l[0].focus();
        }
      });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.stopPropagation();
          set(false);
          toggle.focus();
        }
      });

      /* Tabbing out of the whole item closes it. relatedTarget is null when
         focus leaves the document entirely — treat that as leaving. */
      item.addEventListener('focusout', function (e) {
        if (e.relatedTarget && item.contains(e.relatedTarget)) return;
        if (item.matches(':hover')) return;
        set(false);
      });
    });

    /* Click anywhere outside every menu closes all of them. Bound on the
       document so it also catches clicks on the page behind the panel. */
    document.addEventListener('click', function (e) {
      var inside = items.some(function (i) { return i.contains(e.target); });
      if (!inside) closeAll(null);
    });

    /* Leaving the desktop breakpoint hands navigation to the drawer; any
       open panel must not survive the transition. */
    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener('change', function () { closeAll(null); });
    }
  })();

  /* ---------------------------------------------------------- mobile drawer
     Focus trap, Escape, inert when closed, body scroll lock.               */
  (function drawer() {
    var drawer = document.querySelector('[data-kudu-drawer]');
    var openers = document.querySelectorAll('[data-kudu-draweropen]');
    var closers = document.querySelectorAll('[data-kudu-drawerclose]');
    if (!drawer || !openers.length) return;

    var lastFocus = null;
    var SELECTOR = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function focusables() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll(SELECTOR),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function setOpen(state) {
      drawer.setAttribute('data-open', state ? 'true' : 'false');
      document.body.setAttribute('data-drawer-open', state ? 'true' : 'false');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', state ? 'true' : 'false'); });

      if (state) {
        drawer.removeAttribute('inert');
        drawer.removeAttribute('aria-hidden');
        lastFocus = document.activeElement;
        /* Focus must wait TWO frames, and the count is measured, not guessed.

           The drawer is `visibility: hidden` with a 280ms transition, and it
           carries `inert` while closed. Calling .focus() in the same tick as
           removing those silently fails: focus stays on the burger, so the Tab
           trap below has nothing to trap and a keyboard user opens the menu
           without ever entering it. Measured before the fix: activeElement was
           BUTTON.k-burger with six focusable links inside the open drawer.

           One rAF is not enough either — the style change has not been
           committed by then. Tested over four trials each: single rAF 0/4,
           double rAF 4/4. */
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            if (drawer.getAttribute('data-open') !== 'true') return;
            var f = focusables();
            if (f.length) f[0].focus();
          });
        });
      } else {
        if (drawer.contains(document.activeElement) && lastFocus) lastFocus.focus();
        drawer.setAttribute('inert', '');
        drawer.setAttribute('aria-hidden', 'true');
      }
    }

    setOpen(false);

    openers.forEach(function (o) {
      o.addEventListener('click', function (e) { e.preventDefault(); setOpen(true); });
    });
    closers.forEach(function (c) {
      c.addEventListener('click', function (e) { e.preventDefault(); setOpen(false); });
    });

    document.addEventListener('keydown', function (e) {
      if (drawer.getAttribute('data-open') !== 'true') return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;

      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    mqDesktop.addEventListener('change', function (e) {
      if (e.matches && drawer.getAttribute('data-open') === 'true') setOpen(false);
    });

    /* accordion groups inside the drawer */
    drawer.querySelectorAll('[data-kudu-drawertoggle]').forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });
    });
  })();

  /* ----------------------------------------------------- footer accordions
     The footer listed 35 links as four permanently open columns. On a phone
     that was three-plus screens of footer. Below the column breakpoint the
     groups collapse; at and above it they are columns and this does nothing.

     PROGRESSIVE ENHANCEMENT, and the reason the markup ships OPEN. The panels
     render with data-open="true" and the buttons with aria-expanded="true",
     which is TRUE at parse time — without this script the panels stay open and
     CSS keeps the plain <p> labels instead of the buttons, so no control is
     ever shown that cannot work. Collapsing is this function's job, and it
     sets both attributes together so the DOM never disagrees with the pixels.

     Groups toggle independently, matching the mobile drawer. A footer that
     closed the group you just opened would be hostile.                      */
  (function footerAccordions() {
    var groups = Array.prototype.slice.call(document.querySelectorAll('[data-kudu-footertoggle]'));
    if (!groups.length) return;

    var mqCols = window.matchMedia('(min-width: 1024px)');

    function panelOf(btn) {
      return document.getElementById(btn.getAttribute('aria-controls'));
    }

    function set(btn, open) {
      var panel = panelOf(btn);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.setAttribute('data-open', open ? 'true' : 'false');
    }

    /* At the column breakpoint every panel must be open and every button must
       SAY it is open, because CSS shows the panel there regardless. Leaving a
       stale aria-expanded="false" behind on a desktop resize would tell a
       screen reader the opposite of what is on screen. */
    function sync() {
      if (mqCols.matches) {
        groups.forEach(function (btn) { set(btn, true); });
      } else {
        groups.forEach(function (btn) { set(btn, false); });
      }
    }

    groups.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (mqCols.matches) return;
        set(btn, btn.getAttribute('aria-expanded') !== 'true');
      });
    });

    sync();
    if (mqCols.addEventListener) mqCols.addEventListener('change', sync);
  })();

  /* ------------------------------------------------------------- sticky CTA
     Measured from offsetHeight and written back as --sticky-h, so the space
     reserved at the foot of the page shrinks by itself when the bar carries
     one button instead of two.                                             */
  (function sticky() {
    var bar = document.querySelector('[data-kudu-sticky]');
    if (!bar) return;

    function measure() {
      if (mqDesktop.matches) {
        document.documentElement.style.setProperty('--sticky-h', '0px');
        return;
      }
      document.documentElement.style.setProperty('--sticky-h', bar.offsetHeight + 'px');
    }

    /* THE THIRD CTA. The footer carries the same primary button, so once it is
       on screen the sticky bar was covering a live copy of itself with a
       shorter-worded duplicate — two calls to action competing at the moment
       the visitor had finally reached the one in the page. The bar now stands
       down while the footer CTA is visible and returns on scroll back up. */
    var atFooter = false;
    var footerCta = document.querySelector('.k-footer__cta');
    if (footerCta && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { atFooter = e.isIntersecting; });
        check();
      }, { threshold: 0 }).observe(footerCta);
    }

    var shown = false;
    function check() {
      var past = scrollPos() > 520 && !atFooter;
      if (past === shown) return;
      shown = past;
      bar.setAttribute('data-visible', past ? 'true' : 'false');
    }

    bar.setAttribute('data-visible', 'false');
    measure();
    check();

    onScroll(check);
    window.addEventListener('resize', measure, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(measure).observe(bar);
  })();

  /* -------------------------------------------------------- scroll reveal */
  (function reveal() {
    var els = document.querySelectorAll('.k-reveal');
    if (!els.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.setAttribute('data-revealed', 'true'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-revealed', 'true');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    els.forEach(function (el) { io.observe(el); });
  })();

  /* --------------------------------------------------- one FAQ at a time
     Progressive enhancement only. Without JS every <details> still opens,
     just not exclusively.                                                  */
  (function faq() {
    document.querySelectorAll('[data-kudu-faq]').forEach(function (group) {
      var items = group.querySelectorAll('details');
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) { if (other !== item) other.open = false; });
        });
      });
    });
  })();

})();
